import { type PlannedOutcome, type SyncAction, type SyncPlan } from "./types";

export const actionsOrder: { [key in SyncAction]: number } = {
	"create-publish-repo": 0,
	"initialize-git-repo": 1,
	"commit-initial-changes": 2,
	"commit-outstanding-changes": 3,
	"create-remote": 4,
	"set-tracking-branch": 5,
	"push-to-remote": 6,
	"publish-document": 7,
	// this needs to happen after the permission is created.
	"ensure-reboot-tables-created": 8,
	"revalidate-document": 9,
	// we only need to do this for the initial creation
	"provisioning-publish-url": 10,
} as const;

//
// ALL WORKFLOWS ARE LISTED BELOW
//
// TODO - we might want to refactor to make a workflow per file in the future.
//

export function actionIsPlanned(plan: SyncPlan | undefined | null, action: SyncAction): boolean {
	return plan?.actions.includes(action) ?? false;
}

export const emptySyncPlan = (): SyncPlan => ({
	planStatus: "status-pending",
	publishStatus: "status-pending",
	actions: [],
	plannedOutcome: null,
	branch: undefined,
	ahead: 0,
	behind: 0,
});

// // This is used for 1 to 1 sharing rather than using public sharing.
export const shareSyncPlan = ({
	isOnPublishBranch,
	remoteRepoExists,
	gitRepoStatus,
	documentRemoteSet,
	trackingBranchSet,
	repoClean,
	repoAhead,
	hasBaseTables,
}) => {
	const actions = new Set<SyncAction>();
	let plannedOutcome: PlannedOutcome = null;
	const shouldAutoPush = isOnPublishBranch && remoteRepoExists === true;

	//
	// Attempt to add the actions to the action set in roughly the order they'd be executed.
	//
	if (!remoteRepoExists) {
		actions.add("create-publish-repo");
		actions.add("create-remote");
		actions.add("set-tracking-branch");
		actions.add("provisioning-publish-url");
	}

	// first evaluate local sync actions (e.g. commit)
	if (!gitRepoStatus) {
		actions.add("initialize-git-repo");
		actions.add("commit-initial-changes");
		plannedOutcome = "commit";
	}

	if (!repoClean) {
		actions.add("commit-outstanding-changes");
		plannedOutcome = "commit";
	}

	// Actions is passed by reference and is mutated in this function.
	plannedOutcome = handleRemotes(
		plannedOutcome,
		shouldAutoPush,
		false,
		gitRepoStatus,
		trackingBranchSet,
		repoClean,
		repoAhead,
		documentRemoteSet,
		actions
	);

	if (hasBaseTables) {
		actions.add("ensure-reboot-tables-created");
	}

	return [
		[...actions].sort((a, b) => actionsOrder[a] - actionsOrder[b]),
		plannedOutcome,
	] as const;
};

export const publishSyncPlan = ({
	syncMode,
	isOnPublishBranch,
	remoteRepoExists,
	gitRepoStatus,
	documentRemoteSet,
	trackingBranchSet,
	repoClean,
	repoAhead,
	isPublished,
	hasBaseTables,
}) => {
	const actions = new Set<SyncAction>();
	let plannedOutcome: PlannedOutcome = null;

	const shouldAutoPublish = syncMode === "publish-if-unpublished" && isOnPublishBranch;
	const shouldAutoPush =
		syncMode === "try-sync" && isOnPublishBranch && remoteRepoExists === true;

	//
	// Attempt to add the actions to the action set in roughly the order they'd be executed.
	//
	if (shouldAutoPublish && remoteRepoExists === false) {
		actions.add("create-publish-repo");
	}

	// first evaluate local sync actions (e.g. commit)
	if (!gitRepoStatus) {
		actions.add("initialize-git-repo");
		actions.add("commit-initial-changes");
		plannedOutcome = "commit";
	}

	if (!repoClean) {
		actions.add("commit-outstanding-changes");
		plannedOutcome = "commit";
	}

	// Actions is passed by reference and is mutated in this function.
	plannedOutcome = handleRemotes(
		plannedOutcome,
		shouldAutoPush,
		shouldAutoPublish,
		gitRepoStatus,
		trackingBranchSet,
		repoClean,
		repoAhead,
		documentRemoteSet,
		actions
	);

	if (shouldAutoPublish && (remoteRepoExists === false || isPublished === false)) {
		actions.add("publish-document");
		plannedOutcome = "publish"; // This plan results in a publish.
	}

	if (plannedOutcome === "push" || plannedOutcome === "publish") {
		actions.add("revalidate-document");
	}

	if (plannedOutcome === "publish" && hasBaseTables) {
		actions.add("ensure-reboot-tables-created");
	}

	return [
		[...actions].sort((a, b) => actionsOrder[a] - actionsOrder[b]),
		plannedOutcome,
	] as const;
};

// This logic is pretty tricky.
// this determines whether if we need to create a remote and set a tracking brach and if we need to push to the remote.
const handleRemotes = (
	prevPlanOutcome: PlannedOutcome,
	shouldAutoPush: boolean,
	shouldAutoPublish: boolean,
	gitRepoStatus,
	trackingBranchSet,
	repoClean,
	repoAhead,
	documentRemoteSet,
	actions
) => {
	let plannedOutcome: PlannedOutcome = prevPlanOutcome;
	// next, should we attempt to go upstream?
	// within this block, we are on the publishing branch (or what will be come it)
	if (shouldAutoPush || shouldAutoPublish) {
		if (!gitRepoStatus || !documentRemoteSet) {
			actions.add("create-remote");
		}
		if (!gitRepoStatus || !documentRemoteSet || !trackingBranchSet) {
			actions.add("set-tracking-branch");
		}

		if (
			// These 3 represent that we initialized the upstream new, so we have to push
			!gitRepoStatus ||
			!documentRemoteSet ||
			!trackingBranchSet ||
			// these 2 states represent that we have changes to send upstream
			!repoClean ||
			repoAhead
		) {
			actions.add("push-to-remote");
			plannedOutcome = "push";
		}
	}
	return plannedOutcome;
};
